import { type FormEvent, useEffect, useRef, useState } from "react";

import { enforceProductLocale } from "../../i18n/config";
import {
  AdminLoginError,
  beginGoogleSignIn,
  cancelLocalAdminLogin,
  completeLocalAdminMfa,
  getFederationAvailability,
  loginAsLocalAdmin,
} from "./api";
import type { Actor } from "@kavoosh/contracts";
import { useAuthCopy } from "./authCatalog";

function Brand() {
  const { t } = useAuthCopy();
  return <span className="login-brand"><span className="login-brand-mark">K</span><span><strong>Kavoosh</strong><small>{t("brand.tagline")}</small></span></span>;
}

type LoginState =
  | { kind: "idle" | "submitting" | "google_submitting" }
  | { kind: "error" | "notice" | "success"; message: string };

export function LoginPage({ onBack, onAuthenticated, onPasswordRecovery }: {
  onBack: () => void;
  onAuthenticated?: (actor: Actor) => void;
  onPasswordRecovery?: () => void;
}) {
  const [identifier, setIdentifier] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [checkingFederation, setCheckingFederation] = useState(false);
  const { t } = useAuthCopy();
  const [state, setState] = useState<LoginState>({ kind: "idle" });
  const federationTimerRef = useRef<number | null>(null);
  const federationAbortRef = useRef<AbortController | null>(null);
  const complete = state.kind === "success";
  const mfaStep = pendingUserId !== null;
  const federationBlocked = mfaStep || ["submitting", "google_submitting", "success"].includes(state.kind);

  function stopFederationDiscovery() {
    if (federationTimerRef.current !== null) {
      window.clearTimeout(federationTimerRef.current);
      federationTimerRef.current = null;
    }
    federationAbortRef.current?.abort();
    federationAbortRef.current = null;
  }

  useEffect(() => {
    enforceProductLocale();
    document.title = t("login.documentTitle");
  }, [t]);

  useEffect(() => {
    if (!mfaStep) {
      setMfaCode("");
      return;
    }
    // A password manager must never carry the previous username into the
    // second-factor control. Reset once when password assurance transitions
    // to MFA; never schedule a later clear that can erase deliberate input.
    setMfaCode("");
  }, [mfaStep]);

  useEffect(() => {
    stopFederationDiscovery();
    if (federationBlocked) {
      setGoogleAvailable(false);
      setCheckingFederation(false);
      return;
    }

    const email = identifier.trim();
    if (!email.includes("@") || email.length > 254) {
      setGoogleAvailable(false);
      setCheckingFederation(false);
      return;
    }

    let active = true;
    setCheckingFederation(true);
    federationTimerRef.current = window.setTimeout(() => {
      federationTimerRef.current = null;
      const controller = new AbortController();
      federationAbortRef.current = controller;
      void getFederationAvailability(email, { signal: controller.signal }).then((availability) => {
        if (active && !controller.signal.aborted) setGoogleAvailable(availability.google);
      }).finally(() => {
        if (federationAbortRef.current === controller) federationAbortRef.current = null;
        if (active && !controller.signal.aborted) setCheckingFederation(false);
      });
    }, 250);

    return () => {
      active = false;
      stopFederationDiscovery();
    };
  }, [federationBlocked, identifier]);

  async function leaveLogin() {
    stopFederationDiscovery();
    setCheckingFederation(false);
    if (mfaStep) await cancelLocalAdminLogin().catch(() => undefined);
    onBack();
  }

  async function restartLogin() {
    stopFederationDiscovery();
    setCheckingFederation(false);
    await cancelLocalAdminLogin().catch(() => undefined);
    setPendingUserId(null);
    setIdentifier("");
    setMfaCode("");
    setShowPassword(false);
    setState({ kind: "idle" });
  }

  async function startGoogle() {
    const email = identifier.trim();
    if (!googleAvailable || !email.includes("@")) return;
    stopFederationDiscovery();
    setCheckingFederation(false);
    setState({ kind: "google_submitting" });
    try {
      const material = await beginGoogleSignIn(email);
      window.location.assign(material.authorization_url);
    } catch (error) {
      setState({
        kind: "error",
        message: error instanceof AdminLoginError
          ? error.message
          : t("login.googleError"),
      });
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    stopFederationDiscovery();
    setCheckingFederation(false);
    setGoogleAvailable(false);
    setState({ kind: "submitting" });

    try {
      if (pendingUserId) {
        const code = mfaCode.trim();
        const actor = await completeLocalAdminMfa({ code, expectedUserId: pendingUserId });
        form.reset();
        setMfaCode("");
        setPendingUserId(null);
        setState({ kind: "success", message: t("login.mfaSuccess") });
        onAuthenticated?.(actor);
        return;
      }

      const password = String(fields.get("password") ?? "");
      const result = await loginAsLocalAdmin({ identifier, password });
      form.reset();
      setIdentifier("");
      setMfaCode("");
      setShowPassword(false);
      if (result.mfa_required) {
        setPendingUserId(result.actor.user_id);
        setState({ kind: "notice", message: t("login.mfaRequired") });
        return;
      }
      setState({ kind: "success", message: t("login.success") });
      onAuthenticated?.(result.actor);
    } catch (error) {
      if (!pendingUserId) {
        form.reset();
        setShowPassword(false);
      }
      setState({
        kind: "error",
        message: error instanceof AdminLoginError
          ? error.message
          : t("login.error"),
      });
    }
  }

  const busy = state.kind === "submitting" || state.kind === "google_submitting";

  return (
    <main className="login-page">
      <aside className="login-aside">
        <button className="login-brand-button" type="button" onClick={() => void leaveLogin()} aria-label={t("login.back")}><Brand /></button>
        <div className="login-aside-copy"><span className="login-eyebrow">{t("login.secureAccess")}</span><h1>{t("login.asideTitleLine1")}<br />{t("login.asideTitleLine2")}</h1><p>{t("login.asideBody")}</p></div>
        <div className="login-principles"><span>◇ {t("login.principleIdentity")}</span><span>◈ {t("login.principlePrivacy")}</span><span>✓ {t("login.principleAudit")}</span></div>
      </aside>
      <section className="login-main" aria-labelledby="login-title">
        <div className="login-top-actions">
          <button className="login-back" type="button" onClick={() => void leaveLogin()}>← {t("login.back")}</button>
        </div>
        <form className="login-card" onSubmit={submit} aria-busy={busy}>
          <div className="login-mobile-brand"><Brand /></div>
          <span className="login-lock" aria-hidden="true">⌾</span>
          <h2 id="login-title">{mfaStep ? t("login.mfaTitle") : t("login.title")}</h2>
          <p className="login-intro">{mfaStep ? t("login.mfaIntro") : t("login.intro")}</p>
          {(state.kind === "error" || state.kind === "notice" || state.kind === "success") && (
            <div className={`login-alert ${state.kind === "error" ? "error" : "success"}`} role={state.kind === "error" ? "alert" : "status"}>{state.message}</div>
          )}
          <fieldset disabled={busy || complete}>
            {mfaStep ? <>
              <input
                className="login-autofill-identity"
                type="email"
                name="username"
                value={identifier}
                readOnly
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
              />
              <label htmlFor="login-mfa-code">{t("login.mfaCode")}</label>
              <input
                key={`mfa-${pendingUserId}`}
                id="login-mfa-code"
                name="mfa_code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCapitalize="none"
                spellCheck={false}
                required
                minLength={6}
                maxLength={64}
                autoFocus
                placeholder={t("login.mfaPlaceholder")}
                value={mfaCode}
                onChange={(event) => {
                  const nextValue = event.currentTarget.value;
                  setMfaCode(nextValue.includes("@") ? "" : nextValue);
                }}
              />
              <button className="login-primary" type="submit">{state.kind === "submitting" ? t("login.mfaSubmitting") : t("login.mfaSubmit")}</button>
              <button className="login-forgot" type="button" onClick={() => void restartLogin()}>{t("login.mfaCancel")}</button>
            </> : <>
              <label htmlFor="login-email">{t("login.identifier")}</label>
              <input id="login-email" name="email" type="email" autoComplete="username" required maxLength={254} placeholder={t("login.identifierPlaceholder")} value={identifier} onChange={(event) => setIdentifier(event.currentTarget.value)} />
              <span className="login-label-row">
                <label htmlFor="login-password">{t("login.password")}</label>
                <button className="login-forgot" type="button" onClick={() => onPasswordRecovery?.()}>{t("login.forgot")}</button>
              </span>
              <span className="login-password-field">
                <input id="login-password" name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" required placeholder={t("login.passwordPlaceholder")} />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-controls="login-password">{showPassword ? t("login.hide") : t("login.show")}</button>
              </span>
              <button className="login-primary" type="submit">{state.kind === "submitting" ? t("login.submitting") : t("login.submit")}</button>
              {(googleAvailable || checkingFederation) && <div className="login-divider"><span>{t("login.or")}</span></div>}
              {googleAvailable && <button className="login-sso" type="button" onClick={() => void startGoogle()}><b aria-hidden="true">G</b>{state.kind === "google_submitting" ? t("login.googleStarting") : t("login.google")}</button>}
            </>}
          </fieldset>
          <p className="login-discovery">{mfaStep ? t("login.mfaPrivacy") : t("login.privacy")}</p>
          {!mfaStep && <p className="login-registration">{t("login.noRegistration")}</p>}
        </form>
        <div className="login-footer">{t("login.footer")}</div>
      </section>
    </main>
  );
}
